export function isTokenExpired(expiresAt: string | null | undefined): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() <= Date.now();
}

export function formatDate(date?: string | null, locale = 'en-GB'): string {
  if (!date) return '';
  try {
    const d = new Date(date);
    const dayName = d.toLocaleDateString(locale, { weekday: 'short' });
    const month = d.toLocaleDateString(locale, { month: 'short' });
    const day = d.getDate();
    return `${dayName}, ${month} ${day}`;
  } catch {
    return date;
  }
}

export function formatActivityDate(dateStr: string | null | undefined, locale = 'en-GB'): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const month = d.toLocaleString(locale, { month: 'short' }); //.toUpperCase();
    return `${month} ${d.getDate()}, ${d.getFullYear()}`;
  } catch {
    return dateStr ?? '';
  }
}

export function formatDateMinimal(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const [year, month, day] = value.split('T')[0].split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

/**
 * Same shape as `formatDate` but accepts a `Date` object directly.
 * Returns e.g. "Mon, Jan 5". Call `.toUpperCase()` at the site when needed.
 */
export function formatShortDate(date: Date, locale = 'en-GB'): string {
  const dayName = date.toLocaleDateString(locale, { weekday: 'short' });
  const month = date.toLocaleDateString(locale, { month: 'short' });
  const day = date.getDate();
  return `${dayName}, ${month} ${day}`;
}

export function formatTimeFromDate(date?: string | null): string {
  if (!date || !date.includes('T')) return '';
  try {
    const d = new Date(date);
    const h = d.getHours();
    const m = d.getMinutes();
    const period = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')}${period}`;
  } catch {
    return '';
  }
}

export function formatDateTimeFromDate(date?: string | null, locale = 'en-GB'): string {
  const d = formatDate(date, locale);
  const t = formatTimeFromDate(date);
  if (!d && !t) return '';
  if (!t) return d;
  if (!d) return t;
  return `${d}, ${t}`;
}

/**
 * Short weekday + day + month label for wheel-style date pickers, e.g. "Mon 7 Sep".
 * Day-before-month order, unlike `formatShortDate` (which reads "Mon, Jan 5").
 */
export function formatWheelDay(date: Date, locale = 'en-GB'): string {
  const dayName = date.toLocaleDateString(locale, { weekday: 'short' });
  const month = date.toLocaleDateString(locale, { month: 'short' });
  return `${dayName} ${date.getDate()} ${month}`;
}

export function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, '0')}${period}`;
}

/**
 * `formatTime` for the UTC hour of an instant, e.g. a `2026-08-29T09:00:00Z` slot → `9:00am`.
 *
 * Read in UTC on purpose: Mews expresses an hourly service's time units in UTC, so the label has to
 * name the slot the user actually tapped rather than its local-time shadow.
 */
export function formatUtcHour(date: Date): string {
  return formatTime(`${String(date.getUTCHours()).padStart(2, '0')}:00`);
}

/**
 * Day plus UTC hour, e.g. `Sat, Aug 29, 9:00am` — how an hourly Mews reservation reads back to the
 * guest. Shared so the booking form row and the category step cannot drift apart.
 */
export function formatUtcDateAndHour(date: Date): string {
  return `${formatDate(date.toISOString())}, ${formatUtcHour(date)}`;
}

export function timePartIconFromTime(time: string | null | undefined): string {
  if (!time) return 'Clock';
  try {
    const hour = parseInt(time.split(':')[0], 10);
    if (hour >= 6 && hour < 12) return 'SunLight';
    if (hour >= 12 && hour < 18) return 'SunLight';
    return 'HalfMoon';
  } catch {
    return 'Clock';
  }
}

export function timePartIconFromDate(date?: string | null): string {
  if (!date || !date.includes('T')) return 'Clock';
  try {
    const h = new Date(date).getHours();
    if (h >= 6 && h < 12) return 'SunLight';
    if (h >= 12 && h < 18) return 'SunLight';
    return 'HalfMoon';
  } catch {
    return 'Clock';
  }
}

/**
 * Normalises a Date to UTC midnight while preserving the **local** calendar day.
 *
 * Call this immediately when a date arrives from a date-picker so that the
 * stored Date object is timezone-neutral: `toISOString()` will then always
 * produce the correct date string regardless of the user's UTC offset.
 *
 * Example (UTC+1): picker returns 2026-05-28 00:00 local → this returns
 * a Date whose `toISOString()` gives "2026-05-28T00:00:00.000Z".
 */
export function toUtcMidnight(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}
